import { useEffect, useRef, useState } from 'react'
import { MapPin, ChevronDown } from 'lucide-react'
import { useMe } from '../../auth/hooks/useMe'
import { ChangeLocationDrawer } from './ChangeLocationDrawer'

/**
 * Compact pill showing the engineer's current working postcode. Tapping it
 * opens the {@link ChangeLocationDrawer} — a bottom sheet where they enter a
 * new postcode (DEV-48214 §5.4A). Lives in the shared EngineerHeader so the
 * change-location affordance is available on every engineer page.
 *
 * Postcode source (first non-empty wins):
 *   1. `sessionStorage.engineerSession.postcode` — the value in use this
 *      browser tab.
 *   2. `useMe().last_postcode` — the user's cross-device fallback.
 *
 * Renders nothing when neither is available (engineer has never set a
 * postcode; JobSetup will prompt them at the appropriate route).
 *
 * Changing location keeps the basket (DEV-51046). The drawer says so up
 * front and confirms it with a toast once the move goes through.
 */
export function PostcodePill() {
  const { data: meData } = useMe()
  const [sessionPostcode, setSessionPostcode] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  // Focus comes back here when the drawer closes (AC7).
  const pillRef = useRef<HTMLButtonElement>(null)

  // Mirror sessionStorage into React state on mount and whenever another tab
  // updates it. sessionStorage doesn't fire storage events in the same tab —
  // that's fine, JobSetup / the drawer write to it and we re-read on mount.
  useEffect(() => {
    const readSession = () => {
      try {
        const raw = sessionStorage.getItem('engineerSession')
        if (!raw) {
          setSessionPostcode(null)
          return
        }
        const parsed = JSON.parse(raw) as { postcode?: string }
        setSessionPostcode(parsed?.postcode || null)
      } catch {
        setSessionPostcode(null)
      }
    }
    readSession()
    window.addEventListener('storage', readSession)
    return () => window.removeEventListener('storage', readSession)
  }, [])

  const postcode = sessionPostcode || meData?.last_postcode || null
  if (!postcode) {
    return null
  }

  return (
    <>
      <button
        ref={pillRef}
        type="button"
        onClick={() => setIsDrawerOpen(true)}
        // 44px pill, fill inverted from the nav tokens (DEV-48214): background
        // is `--nav-text` and content is `--nav-bg`, so contrast holds whether
        // the tenant picked a dark or light nav. Both vars are set globally by
        // `applyBrandColors()` (defaults in tokens.css). A chevron chip signals
        // the pill opens a picker.
        className="inline-flex items-center gap-2 h-11 pl-3.5 pr-2 rounded-full text-[15px] font-bold cursor-pointer transition-transform active:scale-[0.98] min-w-0"
        style={{
          backgroundColor: 'var(--nav-text)',
          color: 'var(--nav-bg)',
        }}
        aria-label={`Change working postcode. Currently ${postcode}.`}
      >
        <MapPin className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true" />
        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
          {postcode}
        </span>
        <span
          className="inline-flex items-center justify-center h-[30px] px-2 rounded-full flex-shrink-0"
          style={{
            backgroundColor: 'color-mix(in oklab, var(--nav-bg) 12%, transparent)',
          }}
          aria-hidden="true"
        >
          <ChevronDown className="w-4 h-4" strokeWidth={2.4} />
        </span>
      </button>

      <ChangeLocationDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        currentPostcode={postcode}
        returnFocusRef={pillRef}
      />
    </>
  )
}
